import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { AlertTriangle, RefreshCw, Search, WifiOff, Zap, ZapOff } from "lucide-react";
import { useCurrentUser } from "@/lib/auth";
import {
  FERNAUSLESE_SUPPLY_FAULT_HINT,
  FERNAUSLESE_SUPPLY_FAULT_LABEL,
  fernausleseStateLabel,
  getFernausleseOverview,
  isFernausleseChannelOn,
  isFernausleseSupplyFault,
  type FernausleseOverviewRow,
} from "@/lib/fernausleseApi";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Table } from "@/components/ui/Table";

type MeterFilter = "all" | "problems" | "on" | "occupied" | "free";

const FILTERS: { id: MeterFilter; label: string }[] = [
  { id: "all", label: "Alle" },
  { id: "problems", label: "Störungen" },
  { id: "on", label: "Strom an" },
  { id: "occupied", label: "Belegt" },
  { id: "free", label: "Frei" },
];

const LIVE_REFRESH_MS = 60_000;

function formatKwh(value: string | null | undefined): string {
  if (!value) return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `${num.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 3 })} kWh`;
}

function formatPower(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${value.toLocaleString("de-DE")} W`;
}

function formatClock(date: Date): string {
  return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

function pitchCountLabel(count: number): string {
  return count === 1 ? "1 Platz" : `${count} Plätze`;
}

function channelCaption(row: FernausleseOverviewRow): string {
  const channel = row.channel_id != null ? `Kanal ${row.channel_id}` : "Kein Kanal";
  const label = row.live?.label?.trim();
  if (!label) return channel;
  const normalized = label.toLowerCase().replace(/^platz\s+/, "");
  if (normalized === row.pitch_code.toLowerCase()) return channel;
  return `${channel} · ${label}`;
}

function hasSupplyFault(row: FernausleseOverviewRow): boolean {
  return isFernausleseSupplyFault(row.live?.error);
}

function hasQueryFault(row: FernausleseOverviewRow): boolean {
  return Boolean(row.live?.api_error);
}

function hasProblem(row: FernausleseOverviewRow): boolean {
  return hasSupplyFault(row) || hasQueryFault(row);
}

function matchesFilter(row: FernausleseOverviewRow, filter: MeterFilter): boolean {
  switch (filter) {
    case "problems":
      return hasProblem(row);
    case "on":
      return isFernausleseChannelOn(row.live?.state);
    case "occupied":
      return Boolean(row.guest_name);
    case "free":
      return !row.guest_name;
    default:
      return true;
  }
}

function matchesSearch(row: FernausleseOverviewRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [row.pitch_code, row.channel_id?.toString(), row.guest_name, row.booking_number, row.live?.label]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

function compareRows(a: FernausleseOverviewRow, b: FernausleseOverviewRow): number {
  const rank = (row: FernausleseOverviewRow) => (hasSupplyFault(row) ? 0 : hasQueryFault(row) ? 1 : 2);
  const byRank = rank(a) - rank(b);
  if (byRank !== 0) return byRank;
  return a.pitch_code.localeCompare(b.pitch_code, "de", { numeric: true, sensitivity: "base" });
}

function MeterStatus({ row }: { row: FernausleseOverviewRow }) {
  const live = row.live;
  if (live?.api_error) {
    return (
      <div className="space-y-1">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-status-orange px-2.5 py-1 text-xs font-semibold text-black">
          <WifiOff size={14} aria-hidden />
          Nicht erreichbar
        </span>
        <p className="max-w-[16rem] text-xs text-label-secondary" title={live.api_error}>
          {live.api_error}
        </p>
      </div>
    );
  }
  if (!live || live.state == null) {
    return <span className="text-label-tertiary">Kein Live-Stand</span>;
  }
  if (hasSupplyFault(row)) {
    const on = isFernausleseChannelOn(live.state);
    return (
      <div className="space-y-1">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-status-red px-2.5 py-1 text-xs font-semibold text-white">
          <AlertTriangle size={14} aria-hidden />
          {FERNAUSLESE_SUPPLY_FAULT_LABEL}
        </span>
        <p className="text-xs font-medium text-status-red">Im Schrank prüfen</p>
        <p className={clsx("text-xs", on ? "text-label-secondary" : "text-label-tertiary")}>
          Schaltung: {fernausleseStateLabel(live.state)}
        </p>
      </div>
    );
  }
  const on = isFernausleseChannelOn(live.state);
  return (
    <span className="inline-flex items-center gap-1.5">
      {on ? (
        <Zap size={14} className="text-status-green" aria-hidden />
      ) : (
        <ZapOff size={14} className="text-label-tertiary" aria-hidden />
      )}
      <span className={clsx("text-sm font-medium", on ? "text-status-green" : "text-label-secondary")}>
        {fernausleseStateLabel(live.state)}
      </span>
    </span>
  );
}

export function ElectricityOverviewPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const isReadonly = (user?.groups as string[] | undefined)?.includes("readonly") ?? false;

  const [rows, setRows] = useState<FernausleseOverviewRow[]>([]);
  const [configured, setConfigured] = useState(false);
  const [liveLoaded, setLiveLoaded] = useState(false);
  const [mappedChannels, setMappedChannels] = useState(0);
  const [catalogChannels, setCatalogChannels] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<MeterFilter>("all");
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [focusedPitchId, setFocusedPitchId] = useState<string | null>(null);
  const inFlight = useRef(false);

  const load = useCallback((live = true, silent = false, fresh = false) => {
    if (silent && inFlight.current) return;
    inFlight.current = true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    getFernausleseOverview(live, fresh)
      .then((data) => {
        setRows(data.rows);
        setConfigured(data.configured);
        setLiveLoaded(data.live_loaded);
        setMappedChannels(data.mapped_channels);
        setCatalogChannels(data.catalog_channels);
        setUpdatedAt(new Date());
        setError(null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => {
        inFlight.current = false;
        if (!silent) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") load(true, true);
    }, LIVE_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const supplyFaults = useMemo(() => rows.filter(hasSupplyFault).sort(compareRows), [rows]);
  const queryFaults = useMemo(() => rows.filter(hasQueryFault).sort(compareRows), [rows]);
  const problemCount = rows.filter(hasProblem).length;

  useEffect(() => {
    if (supplyFaults.length > 0) {
      setPageMeta({
        title: "Strom · Störung",
        subtitle: `${pitchCountLabel(supplyFaults.length)}: FI oder Sicherung`,
        tone: "danger",
      });
    } else if (queryFaults.length > 0) {
      setPageMeta({
        title: "Strom",
        subtitle:
          queryFaults.length === 1
            ? "1 Zähler nicht erreichbar"
            : `${queryFaults.length} Zähler nicht erreichbar`,
        tone: "warning",
      });
    } else {
      setPageMeta({
        title: "Strom",
        subtitle: "Zählerstände und Schaltzustände",
      });
    }
    return () => setPageMeta(null);
  }, [queryFaults.length, setPageMeta, supplyFaults.length]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      problems: rows.filter(hasProblem).length,
      on: rows.filter((row) => isFernausleseChannelOn(row.live?.state)).length,
      occupied: rows.filter((row) => Boolean(row.guest_name)).length,
      free: rows.filter((row) => !row.guest_name).length,
    }),
    [rows],
  );

  const filtered = useMemo(() => {
    return rows
      .filter((row) => matchesFilter(row, filter) && matchesSearch(row, search))
      .sort(compareRows);
  }, [filter, rows, search]);

  const missingChannelMappings = configured && mappedChannels === 0 && catalogChannels === 0;
  const usingCatalogFallback = configured && mappedChannels === 0 && catalogChannels > 0;
  const allClear = configured && liveLoaded && problemCount === 0 && !error;

  function showPitch(pitchId: string) {
    setFilter("all");
    setSearch("");
    setFocusedPitchId(pitchId);
    window.setTimeout(() => {
      document.getElementById(`meter-${pitchId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    window.setTimeout(() => {
      setFocusedPitchId((current) => (current === pitchId ? null : current));
    }, 2500);
  }

  return (
    <div className="space-y-4">
      {supplyFaults.length > 0 ? (
        <section
          role="alert"
          aria-live="assertive"
          className="rounded-2xl border-2 border-status-red bg-status-red/10 px-4 py-4 shadow-ios sm:px-5 sm:py-5"
        >
          <div className="flex items-start gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-status-red text-white">
              <AlertTriangle size={26} aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-status-red sm:text-xl">Stromversorgung unterbrochen</h2>
              <p className="mt-1 text-sm text-label">
                {pitchCountLabel(supplyFaults.length)} ohne Strom im Schrank. {FERNAUSLESE_SUPPLY_FAULT_HINT}
              </p>
              <ul className="mt-3 flex flex-wrap gap-2">
                {supplyFaults.map((row) => (
                  <li key={row.pitch_id}>
                    <button
                      type="button"
                      onClick={() => showPitch(row.pitch_id)}
                      className="rounded-full bg-status-red px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                    >
                      Platz {row.pitch_code}
                      {row.guest_name ? ` · ${row.guest_name}` : " · frei"}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ) : null}

      {queryFaults.length > 0 ? (
        <section
          role="status"
          className="rounded-2xl border border-status-orange/50 bg-status-orange/10 px-4 py-4 sm:px-5"
        >
          <div className="flex items-start gap-3">
            <WifiOff size={20} className="mt-0.5 shrink-0 text-status-orange" aria-hidden />
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-label">
                {queryFaults.length === 1
                  ? "1 Zähler ist nicht erreichbar"
                  : `${queryFaults.length} Zähler sind nicht erreichbar`}
              </h2>
              <p className="mt-1 text-sm text-label-secondary">
                Die Abfrage bei Fernauslese ist fehlgeschlagen. Der Schaltzustand dieser Plätze ist gerade unbekannt.
              </p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {queryFaults.map((row) => (
                  <li key={row.pitch_id}>
                    <button
                      type="button"
                      onClick={() => showPitch(row.pitch_id)}
                      className="rounded-full bg-status-orange px-3 py-1 text-xs font-semibold text-black"
                    >
                      Platz {row.pitch_code}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ) : null}

      {allClear ? (
        <section
          role="status"
          className="flex items-center gap-3 rounded-2xl border border-status-green/40 bg-status-green/10 px-4 py-3"
        >
          <Zap size={18} className="shrink-0 text-status-green" aria-hidden />
          <p className="text-sm font-medium text-label">Alle Zähler sind in Ordnung.</p>
        </section>
      ) : null}

      {!configured ? (
        <Card bodyClassName="text-sm text-label-secondary">
          Fernauslese ist nicht konfiguriert. Richten Sie die Schnittstelle unter{" "}
          <Link to="/stammdaten/schnittstellen?integration=fernauslese" className="text-accent underline">
            Stammdaten → Schnittstellen → Fernauslese.de
          </Link>{" "}
          ein und importieren Sie die Kanalliste.
        </Card>
      ) : null}

      {missingChannelMappings ? (
        <Card bodyClassName="text-sm text-status-yellow">
          Es sind keine Fernauslese-Kanäle den Stellplätzen zugeordnet. Importieren Sie die Kanalliste unter{" "}
          <Link
            to="/stammdaten/schnittstellen?integration=fernauslese#fernauslese-kanaele"
            className="text-accent underline"
          >
            Stammdaten → Schnittstellen → Fernauslese.de
          </Link>
          , damit Zählerstände und Schaltzustände geladen werden können.
        </Card>
      ) : null}

      {usingCatalogFallback ? (
        <Card bodyClassName="text-sm text-label-secondary">
          Die Kanalliste ist importiert, aber die Zuordnung zu den Stellplätzen fehlt noch. Die Anzeige nutzt
          vorläufig die Kanalbeschreibungen. Bitte die Zuordnung unter{" "}
          <Link
            to="/stammdaten/schnittstellen?integration=fernauslese#fernauslese-kanaele"
            className="text-accent underline"
          >
            Schnittstellen → Fernauslese.de
          </Link>{" "}
          erneut speichern.
        </Card>
      ) : null}

      {error ? (
        <p className="rounded-xl border border-status-red/40 bg-status-red/10 px-4 py-3 text-sm text-status-red" role="alert">
          {error}
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Störungen"
          value={liveLoaded ? String(problemCount) : "—"}
          hint={
            supplyFaults.length > 0
              ? "FI oder Sicherung"
              : queryFaults.length > 0
                ? "Nicht erreichbar"
                : liveLoaded
                  ? "Keine Störung"
                  : "Warte auf Live-Daten"
          }
          tone={supplyFaults.length > 0 ? "danger" : queryFaults.length > 0 ? "warning" : liveLoaded ? "ok" : "neutral"}
          active={filter === "problems"}
          onClick={() => setFilter(filter === "problems" ? "all" : "problems")}
          emphasize={supplyFaults.length > 0}
        />
        <StatCard
          label="Zähler"
          value={String(counts.all)}
          hint={mappedChannels > 0 ? `${mappedChannels} Kanäle zugeordnet` : "Kanäle"}
          tone="neutral"
          active={false}
          onClick={() => {
            setFilter("all");
            setSearch("");
          }}
        />
        <StatCard
          label="Belegt"
          value={String(counts.occupied)}
          hint={counts.free > 0 ? `${counts.free} frei` : "Plätze mit Gast"}
          tone="neutral"
          active={filter === "occupied"}
          onClick={() => setFilter(filter === "occupied" ? "all" : "occupied")}
        />
        <StatCard
          label="Strom an"
          value={liveLoaded ? String(counts.on) : "—"}
          hint="Eingeschaltete Kanäle"
          tone="neutral"
          active={filter === "on"}
          onClick={() => setFilter(filter === "on" ? "all" : "on")}
        />
      </div>

      <Table
        wide
        toolbar={
          <div className="flex flex-col gap-3 border-b border-separator px-4 py-3 sm:px-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-label-tertiary"
                  aria-hidden
                />
                <input
                  className="eh-input w-full pl-9"
                  placeholder="Platz, Gast, Kanal…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Zähler suchen"
                />
              </div>
              <div className="flex items-center gap-3">
                {updatedAt ? (
                  <span className="text-xs text-label-tertiary">Stand {formatClock(updatedAt)}</span>
                ) : null}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={loading}
                  onClick={() => load(true, false, true)}
                  disabled={isReadonly && !configured}
                  leftIcon={<RefreshCw size={16} aria-hidden />}
                >
                  Aktualisieren
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto pb-0.5">
              <div className="eh-segmented eh-segmented--md min-w-max" role="tablist" aria-label="Zähler filtern">
                {FILTERS.map((item) => {
                  const count = counts[item.id];
                  const alertChip = item.id === "problems" && supplyFaults.length > 0 && filter !== "problems";
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={filter === item.id}
                      className={clsx(
                        "eh-segmented-item inline-flex items-center gap-1.5",
                        filter === item.id && "is-active",
                        alertChip && "text-status-red",
                      )}
                      onClick={() => setFilter(item.id)}
                    >
                      {item.label}
                      {item.id === "problems" && count > 0 ? (
                        <span
                          className={clsx(
                            "rounded-full px-1.5 text-[0.6875rem] font-bold",
                            supplyFaults.length > 0 ? "bg-status-red text-white" : "bg-status-orange text-black",
                          )}
                        >
                          {count}
                        </span>
                      ) : (
                        <span className="tabular-nums text-label-tertiary">{count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        }
      >
        <thead>
          <tr>
            <th>Platz</th>
            <th>Status</th>
            <th>Gast</th>
            <th>Zählerstand</th>
            <th>Verbrauch</th>
            <th>Leistung</th>
            <th>Zielwert</th>
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center text-sm text-label-secondary">
                Laden…
              </td>
            </tr>
          ) : filtered.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center text-sm text-label-secondary">
                {filter === "problems"
                  ? "Keine Störungen. Alle abgefragten Zähler sind in Ordnung."
                  : "Keine Stromzähler für diese Ansicht."}
              </td>
            </tr>
          ) : (
            filtered.map((row) => {
              const fault = hasSupplyFault(row);
              const queryFault = hasQueryFault(row);
              return (
                <tr
                  key={row.pitch_id}
                  id={`meter-${row.pitch_id}`}
                  className={clsx(
                    "scroll-mt-20",
                    fault && "eh-meter-row--fault",
                    !fault && queryFault && "eh-meter-row--query",
                    focusedPitchId === row.pitch_id && "eh-meter-row--focus",
                  )}
                >
                  <td data-label="Platz" className="font-medium">
                    <div>{row.pitch_code}</div>
                    <div className="text-xs font-normal text-label-tertiary">{channelCaption(row)}</div>
                  </td>
                  <td data-label="Status">
                    <MeterStatus row={row} />
                  </td>
                  <td data-label="Gast">
                    {row.guest_name ? (
                      <div className="text-sm">
                        <div>{row.guest_name}</div>
                        {row.booking_number ? (
                          <div className="text-xs text-label-tertiary">{row.booking_number}</div>
                        ) : null}
                        {row.wants_electricity === false ? (
                          <div className="text-xs text-status-orange">Strom nicht gebucht</div>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-label-tertiary">Frei</span>
                    )}
                  </td>
                  <td data-label="Zählerstand">{formatKwh(row.live?.value_kwh)}</td>
                  <td data-label="Verbrauch">{formatKwh(row.live?.usage_kwh ?? row.booking_electricity_kwh)}</td>
                  <td data-label="Leistung">{formatPower(row.live?.power_w)}</td>
                  <td data-label="Zielwert">
                    {row.live?.target_kwh && Number(row.live.target_kwh) > 0 ? formatKwh(row.live.target_kwh) : "—"}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  tone,
  active,
  onClick,
  emphasize = false,
}: {
  label: string;
  value: string;
  hint: string;
  tone: "neutral" | "ok" | "warning" | "danger";
  active: boolean;
  onClick: () => void;
  emphasize?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        "rounded-2xl border px-4 py-3 text-left transition",
        tone === "danger" && "border-status-red/60 bg-status-red/10",
        tone === "warning" && "border-status-orange/50 bg-status-orange/10",
        tone === "ok" && "border-status-green/40 bg-status-green/10",
        tone === "neutral" && "border-separator bg-card hover:bg-fill-quaternary",
        active && tone === "danger" && "ring-2 ring-status-red",
        active && tone === "warning" && "ring-2 ring-status-orange",
        active && tone !== "danger" && tone !== "warning" && "ring-2 ring-accent",
      )}
    >
      <div className="text-[0.6875rem] font-semibold uppercase tracking-wide text-label-secondary">{label}</div>
      <div
        className={clsx(
          "mt-1 font-semibold tabular-nums",
          emphasize ? "text-3xl" : "text-2xl",
          tone === "danger" && "text-status-red",
          tone === "warning" && "text-status-orange",
          tone === "ok" && "text-status-green",
          tone === "neutral" && "text-label",
        )}
      >
        {value}
      </div>
      <div className="mt-0.5 text-xs text-label-secondary">{hint}</div>
    </button>
  );
}
